import React, { useState, useEffect } from 'react';
import { 
  Table, Button, Modal, Form, Input, InputNumber, Switch, message, Card, Row, Col,
  Typography, Space, Tag, Spin, Empty, Select, Avatar, Tooltip, Badge, Divider, Statistic,
  DatePicker
} from 'antd';
import { 
  PlusOutlined, GiftOutlined, BarcodeOutlined, PercentageOutlined, 
  UserAddOutlined, CalendarOutlined, CheckCircleOutlined, CloseCircleOutlined,
  ReloadOutlined, SearchOutlined, FilterOutlined, BarChartOutlined, 
  PieChartOutlined, SettingOutlined
} from '@ant-design/icons';
import 'antd/dist/reset.css';
import Chart from 'react-apexcharts';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;

const Coupons = () => {
  const [coupons, setCoupons] = useState([]);
  const [filteredCoupons, setFilteredCoupons] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  // Metrics
  const [metrics, setMetrics] = useState({
    totalCoupons: 0,
    activeCoupons: 0,
    expiringCoupons: 0,
    averageDiscount: 0
  });

  // Chart states
  const [couponStatusData, setCouponStatusData] = useState({
    labels: ['Active', 'Inactive'],
    series: [0, 0]
  });

  const [couponTypeData, setCouponTypeData] = useState({
    categories: ['For Everyone', 'New Users Only'],
    series: [{
      name: 'Coupons',
      data: [0, 0]
    }]
  });

  useEffect(() => {
    fetchCoupons();
  }, []);

  useEffect(() => {
    if (coupons.length > 0) {
      calculateMetrics();
      prepareChartData();
      applyFilters();
    }
  }, [coupons, searchText, filterStatus]);

  const fetchCoupons = async () => {
    setLoading(true);
    try {
      const response = await fetch('https://main-matrixai-server-lujmidrakh.cn-hangzhou.fcapp.run/api/admin/getAllCoupons', {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();
      if (data.success) {
        setCoupons(data.data);
        setFilteredCoupons(data.data);
      }
    } catch (error) {
      message.error('Failed to fetch coupons');
    } finally {
      setLoading(false);
    }
  };

  const calculateMetrics = () => {
    const totalCoupons = coupons.length;
    const activeCoupons = coupons.filter(coupon => coupon.active).length;
    
    // Consider coupons expiring within 7 days as "expiring soon"
    const today = new Date();
    const next7Days = new Date(today);
    next7Days.setDate(today.getDate() + 7);
    const expiringCoupons = coupons.filter(coupon => {
      const validTill = new Date(coupon.valid_till);
      return coupon.active && validTill > today && validTill < next7Days;
    }).length;
    
    const totalAmount = coupons.reduce((sum, coupon) => sum + coupon.coupon_amount, 0);
    const averageDiscount = totalCoupons > 0 ? (totalAmount / totalCoupons).toFixed(2) : 0;

    setMetrics({
      totalCoupons,
      activeCoupons,
      expiringCoupons,
      averageDiscount
    });
  };

  const prepareChartData = () => {
    // Coupon Status Chart
    const activeCoupons = coupons.filter(coupon => coupon.active).length;
    const inactiveCoupons = coupons.length - activeCoupons;
    
    setCouponStatusData({
      labels: ['Active', 'Inactive'],
      series: [activeCoupons, inactiveCoupons]
    });

    // Coupon Type Chart
    const forEveryone = coupons.filter(coupon => coupon.for_everyone).length;
    const forNewUsers = coupons.filter(coupon => coupon.only_new_users).length;
    
    setCouponTypeData({
      categories: ['For Everyone', 'New Users Only'],
      series: [{
        name: 'Coupons',
        data: [forEveryone, forNewUsers]
      }]
    });
  };

  const applyFilters = () => {
    let result = [...coupons];
    
    // Apply search filter
    if (searchText) {
      const lowerSearchText = searchText.toLowerCase();
      result = result.filter(coupon => 
        coupon.coupon_name.toLowerCase().includes(lowerSearchText) ||
        (coupon.description && coupon.description.toLowerCase().includes(lowerSearchText))
      );
    }
    
    // Apply status filter
    if (filterStatus !== 'all') {
      const isActive = filterStatus === 'active';
      result = result.filter(coupon => coupon.active === isActive);
    }
    
    setFilteredCoupons(result);
  };

  const handleSearch = (e) => {
    setSearchText(e.target.value);
  };

  const handleStatusFilterChange = (value) => {
    setFilterStatus(value);
  };

  const resetFilters = () => {
    setSearchText('');
    setFilterStatus('all');
  };

  const handleAddCoupon = async (values) => {
    try {
      // Format the end date to ISO string
      const endDate = values.endDate.format();
      
      const response = await fetch('https://main-matrixai-server-lujmidrakh.cn-hangzhou.fcapp.run/api/admin/addCoupon', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          coupon_name: values.couponName,
          coupon_amount: values.couponAmount,
          valid_till: endDate,
          only_new_users: values.onlyNewUsers || false,
          active: true,
          description: values.description || ''
        }),
      });
      const data = await response.json();
      if (data.success) {
        message.success('Coupon added successfully');
        setIsModalVisible(false);
        form.resetFields();
        fetchCoupons();
      }
    } catch (error) {
      message.error('Failed to add coupon');
    }
  };

  const handleToggleCoupon = async (coupon) => {
    try {
      const response = await fetch('https://main-matrixai-server-lujmidrakh.cn-hangzhou.fcapp.run/api/admin/activateCoupon', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          couponId: coupon.id,
          durationInMinutes: 60 * 24 * 30, // 30 days
          active: !coupon.active,
        }),
      });
      const data = await response.json();
      if (data.success) {
        message.success(`Coupon ${coupon.active ? 'deactivated' : 'activated'} successfully`);
        fetchCoupons();
      }
    } catch (error) {
      message.error('Failed to update coupon status');
    }
  };
  
  const handleRemoveCoupon = async (coupon) => {
    try {
      const response = await fetch('https://main-matrixai-server-lujmidrakh.cn-hangzhou.fcapp.run/api/admin/removeCoupon', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: coupon.id
        }),
      });
      const data = await response.json();
      if (data.success) {
        message.success('Coupon removed successfully');
        fetchCoupons();
      }
    } catch (error) {
      message.error('Failed to remove coupon');
    }
  };

  const columns = [
    {
      title: 'Coupon Code',
      dataIndex: 'coupon_name',
      key: 'coupon_name',
      width: 200,
      render: (text) => (
        <div className="coupon-code">
          <BarcodeOutlined style={{ marginRight: '8px', color: 'var(--primary-color)' }} />
          <span style={{ fontWeight: 'bold', letterSpacing: '0.5px' }}>{text}</span>
        </div>
      ),
    },
    {
      title: 'Amount',
      dataIndex: 'coupon_amount',
      key: 'coupon_amount',
      width: 120,
      render: (amount) => (
        <div style={{ color: 'var(--secondary-color)', fontWeight: 'bold' }}>
          {amount.toFixed(2)}%
        </div>
      ),
    },
    {
      title: 'Valid Period',
      key: 'validity',
      width: 200,
      render: (_, record) => {
        const createDate = new Date(record.created_at);
        const validTill = new Date(record.valid_till);
        const now = new Date();
        const isExpired = validTill < now;
        const isExpiringSoon = !isExpired && validTill < new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
        
        return (
          <div style={{ fontSize: '13px' }}>
            <div>From: {createDate.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })}</div>
            <div style={{ marginTop: '4px', display: 'flex', alignItems: 'center' }}>
              <span>Until: </span>
              <span style={{ 
                marginLeft: '4px', 
                color: isExpired ? 'var(--danger-color)' : (isExpiringSoon ? 'var(--warning-color)' : 'inherit')
              }}>
                {validTill.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })}
              </span>
            </div>
            {isExpiringSoon && !isExpired && (
              <Tag color="warning" style={{ marginTop: '4px', fontSize: '11px' }}>Expiring Soon</Tag>
            )}
            {isExpired && (
              <Tag color="error" style={{ marginTop: '4px', fontSize: '11px' }}>Expired</Tag>
            )}
          </div>
        );
      },
    },
    {
      title: 'Eligibility',
      key: 'eligibility',
      width: 150,
      render: (_, record) => (
        <div style={{ fontSize: '13px' }}>
          {record.for_everyone && (
            <div style={{ marginBottom: '4px' }}>
              <Tag color="blue">
                <UserAddOutlined /> Everyone
              </Tag>
            </div>
          )}
          {record.only_new_users && (
            <div>
              <Tag color="cyan">
                <UserAddOutlined /> New Users
              </Tag>
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      width: 250,
      render: (text) => text || <Text type="secondary">No description</Text>,
    },
    {
      title: 'Status',
      key: 'status',
      width: 120,
      render: (_, record) => (
        <Badge
          status={record.active ? 'success' : 'error'}
          text={
            <span style={{ color: record.active ? 'var(--success-color)' : 'var(--danger-color)' }}>
              {record.active ? 'Active' : 'Inactive'}
            </span>
          }
        />
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Button
            type={record.active ? 'default' : 'primary'}
            icon={record.active ? <CloseCircleOutlined /> : <CheckCircleOutlined />}
            size="small"
            onClick={() => handleToggleCoupon(record)}
          >
            {record.active ? 'Deactivate' : 'Activate'}
          </Button>
          <Button
            type="danger"
            icon={<CloseCircleOutlined />}
            size="small"
            onClick={() => handleRemoveCoupon(record)}
          >
            Remove
          </Button>
        </Space>
      ),
    },
  ];

  // Chart options
  const donutOptions = {
    chart: {
      type: 'donut',
      fontFamily: 'Inter, sans-serif',
      toolbar: {
        show: false
      }
    },
    legend: {
      position: 'bottom'
    },
    dataLabels: {
      enabled: false
    },
    colors: ['var(--success-color)', 'var(--danger-color)'],
    plotOptions: {
      pie: {
        donut: {
          size: '60%',
          labels: {
            show: true,
            name: {
              show: true
            },
            value: {
              show: true,
              fontSize: '22px',
              fontWeight: 600
            },
            total: {
              show: true,
              label: 'Total',
              formatter: function (w) {
                return w.globals.seriesTotals.reduce((a, b) => a + b, 0);
              }
            }
          }
        }
      }
    }
  };

  const barOptions = {
    chart: {
      type: 'bar',
      fontFamily: 'Inter, sans-serif',
      toolbar: {
        show: false
      }
    },
    plotOptions: {
      bar: {
        borderRadius: 4,
        horizontal: false,
        columnWidth: '60%'
      },
    },
    dataLabels: {
      enabled: false
    },
    colors: ['var(--primary-color)'],
    yaxis: {
      title: {
        text: 'Number of Coupons'
      }
    },
    fill: {
      opacity: 1,
      type: 'gradient',
      gradient: {
        shade: 'light',
        type: "vertical",
        shadeIntensity: 0.25,
        gradientToColors: undefined,
        inverseColors: true,
        opacityFrom: 1,
        opacityTo: 0.85,
        stops: [50, 100],
      },
    },
  };

  if (loading && coupons.length === 0) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', padding: '2rem' }}>
        <Spin size="large" tip="Loading coupon data..." spinning={true} />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', background: 'var(--background-primary)', minHeight: '100vh' }}>
      <div style={{ marginBottom: '24px' }}>
        <Title level={3} style={{ color: 'var(--primary-color)', margin: 0 }}>
          <GiftOutlined /> Coupon Management
        </Title>
        <Text type="secondary">
          Create and manage discount coupons for your users
        </Text>
      </div>

      {/* Stats Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} lg={6}>
          <div className="stat-card primary">
            <div>
              <div className="stat-value">{metrics.totalCoupons}</div>
              <div className="stat-label">Total Coupons</div>
            </div>
            <GiftOutlined className="stat-icon" />
          </div>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <div className="stat-card success">
            <div>
              <div className="stat-value">{metrics.activeCoupons}</div>
              <div className="stat-label">Active Coupons</div>
            </div>
            <CheckCircleOutlined className="stat-icon" />
          </div>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <div className="stat-card warning">
            <div>
              <div className="stat-value">{metrics.expiringCoupons}</div>
              <div className="stat-label">Expiring Soon</div>
            </div>
            <CalendarOutlined className="stat-icon" />
          </div>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <div className="stat-card secondary">
            <div>
              <div className="stat-value">{metrics.averageDiscount}%</div>
              <div className="stat-label">Avg. Discount</div>
            </div>
            <PercentageOutlined className="stat-icon" />
          </div>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} lg={12}>
          <Card 
            title="Coupon Status Distribution" 
            className="dashboard-card"
            bodyStyle={{ height: '300px' }}
          >
            {couponStatusData.series.some(value => value > 0) ? (
              <Chart
                options={{
                  ...donutOptions,
                  labels: couponStatusData.labels
                }}
                series={couponStatusData.series}
                type="donut"
                height="280"
              />
            ) : (
              <Empty description="No coupon data available" style={{ marginTop: '80px' }} />
            )}
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card 
            title="Coupon Type Distribution" 
            className="dashboard-card"
            bodyStyle={{ height: '300px' }}
          >
            {couponTypeData.series[0].data.some(value => value > 0) ? (
              <Chart
                options={{
                  ...barOptions,
                  xaxis: {
                    categories: couponTypeData.categories
                  }
                }}
                series={couponTypeData.series}
                type="bar"
                height="280"
              />
            ) : (
              <Empty description="No coupon type data available" style={{ marginTop: '80px' }} />
            )}
          </Card>
        </Col>
      </Row>

      <Card className="dashboard-card" style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', marginBottom: '16px', gap: '16px', flexWrap: 'wrap', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            <Input
              placeholder="Search by name or description"
              value={searchText}
              onChange={handleSearch}
              style={{ width: '250px' }}
              prefix={<SearchOutlined />}
              allowClear
            />
            
            <Select
              value={filterStatus}
              onChange={handleStatusFilterChange}
              style={{ width: '150px' }}
              placeholder="Filter by status"
            >
              <Option value="all">All Status</Option>
              <Option value="active">Active</Option>
              <Option value="inactive">Inactive</Option>
            </Select>
            
            <Button 
              icon={<ReloadOutlined />} 
              onClick={resetFilters}
              type="text"
              title="Reset filters"
            >
              Reset
            </Button>
          </div>
          
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setIsModalVisible(true)}
        >
          Add New Coupon
        </Button>
      </div>

      <Table
        columns={columns}
          dataSource={filteredCoupons}
        rowKey="id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `Total ${total} coupons`
          }}
          scroll={{ x: 1200 }}
          loading={loading}
          locale={{
            emptyText: (
              <Empty description="No coupons found" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            )
          }}
        />
      </Card>

      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <GiftOutlined style={{ color: 'var(--primary-color)' }} />
            <span>Add New Coupon</span>
          </div>
        }
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          onFinish={handleAddCoupon}
          layout="vertical"
          initialValues={{
            forEveryone: true,
            onlyNewUsers: false,
            endDate: dayjs().add(30, 'day')
          }}
        >
          <Form.Item
            name="couponName"
            label="Coupon Code"
            rules={[{ required: true, message: 'Please enter coupon code' }]}
          >
            <Input 
              placeholder="Enter coupon code (e.g. WELCOME50)" 
              prefix={<BarcodeOutlined />}
              style={{ textTransform: 'uppercase' }}
            />
          </Form.Item>

          <Form.Item
            name="couponAmount"
            label="Discount Amount (%)"
            rules={[{ required: true, message: 'Please enter discount amount' }]}
          >
            <InputNumber
              style={{ width: '100%' }}
              min={1}
              max={100}
              placeholder="Enter discount percentage"
              suffix="%"
            />
          </Form.Item>

          <Form.Item
            name="endDate"
            label="Valid Until"
            rules={[{ required: true, message: 'Please select end date' }]}
            tooltip="Select the end date for this coupon"
          >
            <DatePicker 
              style={{ width: '100%' }}
              format="YYYY-MM-DD HH:mm"
              showTime
              disabledDate={(current) => current && current < dayjs().startOf('day')}
            />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
          >
            <Input.TextArea
              placeholder="Enter coupon description"
              rows={3}
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="forEveryone"
                valuePropName="checked"
                label="Available for Everyone"
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="onlyNewUsers"
                valuePropName="checked"
                label="Only for New Users"
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          <Divider />

          <Form.Item>
            <Space style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button onClick={() => setIsModalVisible(false)}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" icon={<PlusOutlined />}>
                Create Coupon
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Coupons;